import {
  ReactElement,
  Suspense,
  SyntheticEvent,
  useEffect,
  useMemo,
  useState
} from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { Skeleton } from 'antd';
import cc from 'classcat';
import { getUserAvatarName, getUserName } from 'utils/account/user-info';
import { AccountsStatus } from 'utils/constants/account/account-status';
import { userActiveStep } from 'utils/constants/account/user-status';
import { casinoEnabled, sportsbookEnabled } from 'utils/constants/app/general';
import { EIconSize } from 'utils/constants/app/ui';
import SpringConfigs from 'utils/constants/swarm/spring-configs';
import { lazy } from 'utils/generic/lazy';
import { copyToClipboard } from 'utils/helpers';
import { isMobile } from 'utils/is-mobile';
import { TField } from 'interfaces/authentication';
import { AccountInfo } from 'components/AccountInfo';
import { GlobalIcon } from 'components/GlobalIcon';
import { AccountsBalance } from 'newcomponents/Shared/account/AccountsBalance';
import { Avatar } from 'newcomponents/UI/Avatar';
import { Tooltip } from 'newcomponents/UI/Tooltip';
import { useFetchAccountParams } from 'newhooks/account/useFetchAccountParams';
import { setUserBonuses } from 'store/actions/user-data';
import { getRegFieldData } from 'store/selectors/app-data';
import { getBonuses, getIsLoggedIn, getUser } from 'store/selectors/user-data';
import './index.less';

const { ShowInformationForGameBreak } = lazy(
  () =>
    import(
      /* webpackChunkName: "account-game-break-info" */ 'newcomponents/Shared/account/ShowInformationForGameBreak'
    )
);

export const UserInfoAndBalance = function (): ReactElement {
  const dispatch = useDispatch();
  const isLoggedIn = useSelector(getIsLoggedIn);
  const bonuses = useSelector(getBonuses);
  const user = useSelector(getUser);
  const { accountParams } = useFetchAccountParams();
  const { t } = useTranslation();
  const regFieldsData = useSelector(getRegFieldData) as TField[];
  const [isCopied, setIsCopied] = useState(false);

  const { is_verified, id } = user;

  const copyInterval = 3000;

  useEffect(() => {
    if (isLoggedIn) {
      if (!bonuses?.freeBonuses && !casinoEnabled) {
        dispatch(setUserBonuses(true));
      }

      if (!bonuses?.casinoBonuses && !sportsbookEnabled) {
        dispatch(setUserBonuses(false));
      }
    }
  }, [isLoggedIn]);

  const status = useMemo(
    () => AccountsStatus.find(item => String(item.key) === String(user.status)),
    [user.status]
  );

  const showUsernameProfile = regFieldsData.find(
    ({ formType }) => formType === 'username'
  )?.editForm?.usernameInMyProfile;

  const shouldShowKycFailedMessage =
    SpringConfigs.DISPLAY_FAILED_KYC_MESSAGE &&
    user.active_step === userActiveStep.USER_ACTIVE_STEP_VALUE_41 &&
    user.active_step_state === userActiveStep.USER_ACTIVE_STEP_STATE_VALUE_3;

  const handleCopyId = (event: SyntheticEvent, id: number) => {
    event.stopPropagation();
    copyToClipboard(`${id}`, true);
    setIsCopied(true);

    const interval = setInterval(() => {
      setIsCopied(false);
      clearInterval(interval);
    }, copyInterval);
  };

  return (
    <div className="userInfoAndBalance">
      <div className="userInfoAndBalance__info">
        {id ? (
          <div className="userInfoAndBalance__info__wrapper">
            <div className="userInfoAndBalance__info__details__icon">
              <div className="userInfoAndBalance__infoDetailsIcon__container">
                <AccountInfo />

                <div className="userInfoAndBalance__info__details__id">
                  <div className="userInfoAndBalance__container">
                    ID: {id}
                    {isCopied ? (
                      <GlobalIcon
                        lib="generic"
                        name="checkCircle"
                        theme="default"
                        color="var(--v3-color-on-primary)"
                        style={{ margin: '0 4px' }}
                        size={EIconSize._16}
                      />
                    ) : (
                      <GlobalIcon
                        lib="generic"
                        name="copy"
                        theme="default"
                        color="var(--v3-color-on-primary)"
                        size={EIconSize._16}
                        style={{ cursor: 'pointer', margin: '0 4px' }}
                        onClick={e => handleCopyId(e, id)}
                      />
                    )}
                  </div>
                  {!!+accountParams?.accountStatusX && status?.value && (
                    <div className={`${status?.styled} statusColorDes`}>
                      {status?.value || ''}
                    </div>
                  )}
                </div>
              </div>
            </div>
            <div className="userInfoAndBalance__avatarContainer">
              {is_verified && (
                <GlobalIcon
                  lib="generic"
                  name="check"
                  theme="default"
                  position="absolute"
                  size={EIconSize._9}
                  className="userInfoAndBalance__info__checkedIcon"
                  useWrapper
                  skeleton={{ detachPassedClassName: true }}
                />
              )}
              <Avatar className="user__avatar" size={EIconSize._64}>
                {getUserAvatarName(user)}
              </Avatar>
            </div>
            <div className="userInfoAndBalance__info__details">
              {!user.owner_id && (
                <Tooltip
                  overlayClassName="userInfoAndBalance__info__tooltip"
                  placement={SpringConfigs.IS_RTL ? 'topRight' : 'topLeft'}
                  title={getUserName(user)}
                >
                  <div className="userInfoAndBalance__userInfo">
                    <div className="userInfoAndBalance__info__details__username">
                      {getUserName(user)}
                    </div>
                  </div>
                </Tooltip>
              )}
              {!!Number(showUsernameProfile) && (
                <Tooltip
                  overlayClassName="userInfoAndBalance__info__tooltip"
                  placement={SpringConfigs.IS_RTL ? 'topRight' : 'topLeft'}
                  title={user.username}
                >
                  <div className="userInfoAndBalance__info--title">
                    {t('account.username')}
                  </div>
                  <div className="userInfoAndBalance__info__details__user">
                    {user.username}
                  </div>
                </Tooltip>
              )}

              {shouldShowKycFailedMessage && (
                <div className="statusColorDes colorRed userInfoAndBalance__kycFailedMessage">
                  {t('account.kycFailed')}
                </div>
              )}
            </div>
          </div>
        ) : (
          <div className="userInfoAndBalance__info__wrapper">
            <Skeleton
              active
              avatar={{ shape: 'circle', size: 56 }}
              title={{ width: '100%' }}
              paragraph={{ rows: 1, width: '50%', style: { margin: 0 } }}
            />
          </div>
        )}
      </div>
      {(+accountParams?.verifiedAccount === 1 ||
        SpringConfigs.HAS_BONUS_ELIGIBLE) &&
        user.is_bonus_allowed !== null &&
        (user.is_bonus_allowed && user.is_verified ? (
          SpringConfigs.HAS_BONUS_ELIGIBLE ? (
            <div className="userInfoAndBalance__info__details__bonus green">
              <GlobalIcon
                lib="generic"
                name="attentionError"
                theme="default"
                size={EIconSize._20}
              />
              <span className="bonus-text">
                {t('account.EligibleForBonus')}
              </span>
            </div>
          ) : null
        ) : (
          <div
            className={cc([
              'userInfoAndBalance__info__details__bonus',
              { gray: user.is_verified, red: !user.is_verified }
            ])}
          >
            <GlobalIcon
              lib="generic"
              name="attentionError"
              theme="default"
              size={EIconSize._20}
            />
            <span className="bonus-text">
              {user.is_verified
                ? t('account.notEligibleForBonus')
                : t('account.verificationRequired')}
            </span>
          </div>
        ))}

      <AccountsBalance />
      {!!user.exclude_date && !isMobile() && (
        <Suspense fallback={null}>
          <ShowInformationForGameBreak />
        </Suspense>
      )}
    </div>
  );
};
